import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import MagneticButton from "./MagneticButton";
import {
  SunDoodle, Cloud, PaperPlane, Star, Squiggle,
  Flower, MusicNote, Scribble, Frog, Heart, Bug, Butterfly,
} from "./Illustrations";

export default function Hero() {
  return (
    <section className="relative pt-28 md:pt-32 pb-10 md:pb-24 overflow-hidden" data-testid="hero">
      {/* Colour-block corner accents (Peppa-style) */}
      <div className="absolute top-16 left-0 h-32 w-24 md:w-40 bg-[#A9CDE5] rounded-r-[120px] opacity-95 -z-0" aria-hidden />
      <div className="absolute top-44 right-0 h-40 w-20 md:w-32 bg-[#F4C734] rounded-l-[120px] opacity-90 -z-0" aria-hidden />

      <HeroDoodles />

      <div className="mx-auto max-w-[1280px] px-5 md:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          <HeroHeadline />
          <HeroCollage />
        </div>
      </div>

      <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 items-center gap-2 text-[11px] text-[#8A8A85] tracking-[0.2em] uppercase">
        <span className="w-8 h-px bg-[#8A8A85]" /> scroll <span className="w-8 h-px bg-[#8A8A85]" />
      </div>
    </section>
  );
}

function HeroDoodles() {
  return (
    <>
      <SunDoodle size={64} color="#F4C734" className="absolute top-24 right-[10%] float-slow hidden md:block" />
      <Cloud size={90} className="absolute top-44 left-[10%] float-med opacity-80 hidden md:block" />
      <Flower size={48} color="#EE8C72" className="absolute top-[58%] left-[6%] wiggle hidden md:block" />
      <Star size={18} className="absolute top-[44%] left-[22%] wiggle" color="#DD2E20" outline="#2C2C2A" />
      <Star size={14} className="absolute top-32 right-[33%] wiggle" color="#8BC547" />
      <MusicNote size={36} className="absolute top-[68%] right-[8%] float-med hidden md:block" color="#3D5C99" />
      <PaperPlane size={36} className="absolute bottom-[16%] right-[20%] float-med hidden md:block" />
      <Bug size={32} className="absolute bottom-[8%] left-[18%] wiggle hidden md:block" />
      <Scribble w={70} className="absolute top-[22%] left-[44%] hidden md:block" color="#F4C734" />
    </>
  );
}

function HeroHeadline() {
  return (
    <div className="lg:col-span-7 relative z-10">
      <p className="eyebrow mb-5 inline-flex items-center gap-2" data-testid="hero-eyebrow">
        <span className="w-1.5 h-1.5 rounded-full bg-[#DD2E20]" />
        Spring · Summer ‘26
      </p>
      <h1 className="font-serif font-medium text-[14vw] sm:text-[88px] md:text-[104px] lg:text-[128px] leading-[0.92] tracking-[-0.035em]" data-testid="hero-headline">
        Made for
        <br />
        <span className="italic" style={{ color: "#8BC547" }}>little</span>
        <span className="inline-block align-middle mx-3 wiggle">
          <SunDoodle size={64} className="inline-block" color="#F4C734" />
        </span>
        <br />
        <span style={{ color: "#DD2E20" }} className="italic">adventures.</span>
      </h1>

      <div className="mt-8 max-w-[460px] text-[#595955] text-lg md:text-xl font-sans leading-relaxed" data-testid="hero-subhead">
        <p><span className="font-medium text-[#2C2C2A]">Polarized.</span> Unbreakable. Light as air.</p>
        <p className="mt-2">Real protection, built for real childhoods.</p>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <MagneticButton>
          <Link
            to="/collections"
            className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18] transition-colors"
            data-testid="hero-cta-shop"
          >
            Shop the collection
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </MagneticButton>
        <Link
          to="/story"
          className="inline-flex items-center gap-2 px-1 py-2 text-[15px] font-medium ink-link"
          data-testid="hero-cta-story"
        >
          Our story →
        </Link>
      </div>

      <p className="hidden md:flex mt-12 items-center gap-2 text-[12px] text-[#8A8A85] max-w-[420px]">
        <Heart size={13} color="#DD2E20" />
        ₹ 999 · Shipping charges extra, as applicable · Polarized · Ages 2–10
      </p>
    </div>
  );
}

function HeroCollage() {
  return (
    <div className="lg:col-span-5 relative">
      <div className="relative">
        <div className="aspect-[4/5] rounded-[28px] overflow-hidden bg-[#F3EADA] tilt lift relative" data-hover="true">
          <img
            src="/lifestyle/hero.jpg"
            alt="Indian child running on the beach at golden hour in SOL Kids sunglasses"
            className="w-full h-full object-cover"
            loading="eager"
            data-testid="hero-image-main"
          />
          {/* Yellow tape strip */}
          <div className="absolute top-3 left-6 right-6 h-4 bg-[#F4C734] rotate-[-3deg] opacity-90" />
        </div>

        {/* Floating mini card */}
        <div className="absolute -bottom-8 -left-6 md:-left-12 w-[180px] md:w-[220px] rotate-[-6deg] rounded-2xl overflow-hidden bg-white shadow-[0_20px_40px_rgba(0,0,0,0.08)] tilt" data-testid="hero-mini-1">
          <img src="/products/hola-kid.jpg" alt="Friendly Indian kid in SOL sunglasses" className="w-full h-full object-cover" />
        </div>

        {/* Speech bubble */}
        <div className="absolute -top-6 md:-top-10 -right-4 md:-right-10 rotate-[6deg] bg-white rounded-2xl shadow-[0_18px_36px_rgba(0,0,0,0.08)] px-4 py-3 max-w-[200px] border-2 border-[#F4C734]" data-testid="hero-mini-2">
        <p className="font-hand text-[22px] leading-tight">"Mama, let curiosity win." ☀️</p>
        </div>

        {/* Frog peeking */}
        <Frog size={48} className="absolute -bottom-3 right-2 wiggle hidden md:block" />
      </div>
    </div>
  );
}
